<script setup>
import {
  ref,
  computed,
  onBeforeMount,
  watch } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useFilesStore } from "@/stores/files";
import DModal from "@/components/shared/DModal.vue";
import CustomFileSystemProvider from "devextreme/file_management/custom_provider";

import {
  DxFileManager,
  DxPermissions,
  DxToolbar,
  // DxContextMenu,
  DxItem,
  DxFileSelectionItem,
  DxItemView,
  DxDetails,
  DxColumn,
  DxNotifications,
} from "devextreme-vue/file-manager";
import _debounce from "lodash.debounce";

const SEARCH_DEBOUNCE_MS = 300;
const filesStore = useFilesStore();
const companyId = useCompanyStore().companyId;
const fileManagerRefName = ref(null);
const searchValue = ref("");
// let rootItems = ref([]);
let selectedFiles = ref([]);
const emit = defineEmits(["filesSelected", "close", "update:visible"]);

const props = defineProps({
  rootType: {
    type: String,
    default: null,
  },
  visible: {
    type: Boolean,
    default: false,
  },
});

watch(
  () => props.rootType,
  () => {
    if (fileManager.value) {
      fileManager.value.refresh();
    }
  },
);

const fileManager = computed(() => {
  return fileManagerRefName.value?.instance;
});

const debouncedOnChange = computed(() => _debounce(onChangeSearch, SEARCH_DEBOUNCE_MS));

const onChangeSearch = async (e) => {
  if (e.target && (e.target.value == "" || e.target.value.length >= 2)) {
    searchValue.value = await e.target.value;
    // fetchFilesGrid();
    if (fileManager.value) {
      fileManager.value.refresh();
    }
  }
};

const getNewFileMenuOptions = () => {
  return {
    items: [],
    onItemClick: () => {},
  };
};

const onContextMenuShowing = (e) => {
  e.component.option("contextMenu.items", []);
  e.cancel = true; // Prevents the menu from appearing at all
};

const clearSearch = async () => {
  searchValue.value = "";
  // fetchFilesGrid();
  if (fileManager.value) {
    fileManager.value.refresh();
  }
};

const customizeIcon = (fileManagerItem) => {
  if (fileManagerItem.dataItem.type == "folder") {
    return (
      '<svg version="1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" enable-background="new 0 0 48 48">' +
      '<path fill="#FFA000" d="M38,12H22l-4-4H8c-2.2,0-4,1.8-4,4v24c0,2.2,1.8,4,4,4h31c1.7,0,3-1.3,3-3V16C42,13.8,40.2,12,38,12z"/>' +
      '<path fill="#FFCA28" d="M42.2,18H15.3c-1.9,0-3.6,1.4-3.9,3.3L8,40h31.7c1.9,0,3.6-1.4,3.9-3.3l2.5-14C46.6,20.3,44.7,18,42.2,18z"/>' +
      "</svg>"
    );
  } else if (fileManagerItem.dataItem.type == "file") {
    const mimeType = fileManagerItem.dataItem.mime_type || "";
    const fileName = fileManagerItem.dataItem.name || "";

    // PDF files
    if (mimeType.includes("pdf") || fileName.toLowerCase().endsWith(".pdf")) {
      return (
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#DC3545" viewBox="0 0 16 16">' +
        '<path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>' +
        '<path d="M4.603 14.087a.8.8 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.7 7.7 0 0 1 1.482-.645 20 20 0 0 0 1.062-2.227 7.3 7.3 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a11 11 0 0 0 .98 1.686 5.8 5.8 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.86.86 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.7 5.7 0 0 1-.911-.95 11.7 11.7 0 0 0-1.997.406 11.3 11.3 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.8.8 0 0 1-.58.029m1.379-1.901q-.25.115-.459.238c-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361q.016.032.026.044l.035-.012c.137-.056.355-.235.635-.572a8 8 0 0 0 .45-.606m1.64-1.33a13 13 0 0 1 1.01-.193 12 12 0 0 1-.51-.858 21 21 0 0 1-.5 1.05zm2.446.45q.226.245.435.41c.24.19.407.253.498.256a.1.1 0 0 0 .07-.015.3.3 0 0 0 .094-.125.44.44 0 0 0 .059-.2.1.1 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a4 4 0 0 0-.612-.053zM8.078 7.8a7 7 0 0 0 .2-.828q.046-.282.038-.465a.6.6 0 0 0-.032-.198.5.5 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822q.036.167.09.346z"/>' +
        "</svg>"
      );
    }

    // CSV files
    if (mimeType.includes("csv") || fileName.toLowerCase().endsWith(".csv")) {
      return (
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#28A745" viewBox="0 0 16 16">' +
        '<path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5"/>' +
        '<path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"/>' +
        "</svg>"
      );
    }

    // Excel files (.xlsx, .xls)
    if (
      mimeType.includes("spreadsheet") ||
      mimeType.includes("excel") ||
      fileName.toLowerCase().endsWith(".xlsx") ||
      fileName.toLowerCase().endsWith(".xls")
    ) {
      return (
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#217346" viewBox="0 0 16 16">' +
        '<path d="M5.884 6.68a.5.5 0 1 0-.768.64L7.349 10l-2.233 2.68a.5.5 0 0 0 .768.64L8 10.781l2.116 2.54a.5.5 0 0 0 .768-.641L8.651 10l2.233-2.68a.5.5 0 0 0-.768-.64L8 9.219l-2.116-2.54z"/>' +
        '<path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>' +
        "</svg>"
      );
    }

    // Default file icon
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-file-earmark" viewBox="0 0 16 16">' +
      '<path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5z"/>' +
      "</svg>"
    );
  }else if (fileManagerItem.dataItem.type == "url") {
    return (
      "bi bi-link-45deg"
    );
  }else if (fileManagerItem.dataItem.type == "integration"){
    return (
      "bi bi-plug"
    );
  }
};

const onSelectionChanged = (e) => {

  // Filter only files, not folders
  selectedFiles.value = e.selectedItems.filter(
    (item) =>
      item.dataItem?.type === "file" ||
      item.dataItem?.type === "url" ||
      item.dataItem?.type === "integration",
  );
};

const onItemClick = async () => {};


const fetchFilesGrid = async (pathInfo) => {
  let data = {
    companyId: companyId,
    payload: {
      search: searchValue.value,
      parent_id: pathInfo?.dataItem?.id,
      root: props.rootType,
      // You might want to pass pathInfo to backend if needed for filtering
      // e.g., type: pathInfo?.dataItem?.typeId
    },
  };

  return await filesStore
    .fetchFilesGrid(data)
    .then((res) => {
      res.data.forEach((item) => {
        if (item.is_folder) {
          item.type = "folder";
          item.isDirectory = true;
          item.isRoot = true; // This might need adjustment if inside categories
          item.hasSubDirectories = true;
        }
      });
      return res.data;
    })
    .catch(() => []);
};

const fileProvider = new CustomFileSystemProvider({
  getItems: (parentDirectory) => {
    // If we are at the very top level
    if (!parentDirectory.path) {
      return fetchFilesGrid(parentDirectory);
    }

    // Otherwise, fetch data based on which "root" we are inside
    // You can check parentDirectory.name or id to filter what to fetch
    // For now, fetching general grid data as requested
    return fetchFilesGrid(parentDirectory);
  },
  // Implement renameItem, deleteItem, etc. if needed
});

const selectFiles = () => {
  const filesToEmit = selectedFiles.value;
  emit("filesSelected", filesToEmit);
  closeModal();
};

const closeModal = () => {
  emit("update:visible", false);
  emit("close");
};

onBeforeMount(() => {
  clearSearch();
});
</script>

<template>
  <DModal
    :visible="props.visible"
    @update:visible="emit('update:visible', $event)"
    :main_classes="['modal-xl']"
    :dialog_classes="[]"
  >
    <template #title>Select Files from Data Library</template>
    <template #body>
      <div class="file-selector-container">
        <p class="mb-3 text-gray-500">
          Select one or more files from your data library to add to this data
          source.
        </p>

        <div v-if="selectedFiles.length > 0" class="alert alert-info mb-3">
          <strong>{{ selectedFiles.length }}</strong> file(s) selected
        </div>

        <DxFileManager
          ref="fileManagerRefName"
          :file-system-provider="fileProvider"
          :height="500"
          root-folder-name="Data Library"
          :selection-mode="'multiple'"
          :customize-thumbnail="customizeIcon"
          @selection-changed="onSelectionChanged"
          @context-menu-item-click="onItemClick"
          @context-menu-showing="onContextMenuShowing($event)"
        >
          <DxNotifications :show-popup="false" />
          <DxPermissions
            :create="true"
            :copy="true"
            :move="true"
            :delete="true"
            :rename="true"
          />

          <DxItemView :show-parent-folder="false">
            <DxDetails>
              <DxColumn data-field="thumbnail" />
              <DxColumn data-field="name" />
              <DxColumn data-field="fileType" caption="Type" :width="95" />
              <DxColumn
                :visible="searchValue ? true : false"
                data-field="path"
                caption="Path"
              />
              <DxColumn caption="Created At" data-field="createdAtFormatted" />
            </DxDetails>
          </DxItemView>

          <DxToolbar>
            <DxItem name="showNavPane" :visible="true" />
            <DxItem name="separator" />
            <!-- <DxItem text="Create new Folder" icon="newfolder" action="create_folder" /> -->
            <!-- <DxItem
              widget="dxMenu"
              location="before"
              :options="getNewFolderMenuOptions"
            /> -->
            <DxItem
              widget="dxMenu"
              location="before"
              :options="getNewFileMenuOptions()"
            />

            <DxItem widget="dxMenu" location="before" />

            <DxItem template="searchPanelTemplate" location="after" />

            <DxItem name="separator" location="after" />
            <DxItem name="switchView" />

            <!-- <DxItem name="separator" location="after"/> -->
            <!-- <DxItem name="refresh"/> -->

            <!-- <DxFileSelectionItem name="refresh"/> -->

            <!-- <DxFileSelectionItem name="rename"/> -->
            <!-- <DxFileSelectionItem name="separator"/> -->
            <!-- <DxFileSelectionItem text="Edit" icon="edit" action="edit_folder_folder_job" /> -->
            <!-- <DxItem name="delete" text="Delete" icon="remove" action="delete_folder_and_job" /> -->
            <!-- <DxFileSelectionItem name="separator"/> -->
            <DxFileSelectionItem name="clearSelection" />
          </DxToolbar>
          <!-- 
          <DxContextMenu>
            <DxItem
              text="Create new Folder"
              icon="newfolder"
              action="create_folder"
            />
            <DxItem text="Upload new file" icon="plus" action="upload_files" />

            <DxItem text="Edit" icon="edit" action="edit_folder_and_file" />
            <DxItem name="move" action="move_folder_and_file" />
            <DxItem
              text="Delete"
              icon="remove"
              action="delete_folder_and_file"
            />
          </DxContextMenu> -->

          <template #searchPanelTemplate>
            <div class="flex custom-search-file-manage-contanier">
              <div class="custom-search-file-manager-icon">
                <span><i class="bi bi-search"></i></span>
              </div>
              <input
                type="text"
                @input="debouncedOnChange($event)"
                :value="searchValue"
                class="d-textarea mr-10"
                placeholder="Search ..."
              />
              <div
                v-if="searchValue"
                @click="clearSearch()"
                class="custom-search-file-manager-remove-icon-contanier"
              >
                <span
                  ><i class="bi bi-x custom-search-file-manager-remove-icon"></i
                ></span>
              </div>
            </div>
          </template>
        </DxFileManager>
      </div>
    </template>
    <template #footer>
      <div class="w-full">
        <div class="seperator text-center"></div>
        <div class="flex justify-end gap-2 mt-3">
          <button class="btn btn-secondary" type="button" @click="closeModal">
            Cancel
          </button>
          <button
            class="btn btn-primary"
            type="button"
            @click="selectFiles"
            :disabled="selectedFiles.length === 0"
          >
            Add
            {{ selectedFiles.length > 0 ? `(${selectedFiles.length})` : "" }}
          </button>
        </div>
      </div>
    </template>
  </DModal>
</template>

<style scoped>
.file-selector-container {
  min-height: 400px;
}
:deep(
    .dx-filemanager-dirs-tree
      .dx-filemanager-focused-item
      .dx-filemanager-file-actions-button
  ),
:deep(
    .dx-filemanager-dirs-tree
      .dx-treeview-item.dx-state-hover
      .dx-filemanager-file-actions-button
  ),
:deep(.dx-filemanager-file-actions-button.dx-widget) {
  display: none !important;
}
</style>
